/**
 * NfcCardScanPopover
 *
 * Passive global listener for NFC card reads.
 * When a wristband is placed on the reader (and no sync is in progress),
 * looks up the wristband and shows a floating card with the person info.
 *
 * Unassigned wristband  → shows "not registered" message.
 * Known person (guest/staff) → shows name, role badge, and stored credit.
 * Auto-dismisses after 8 seconds.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Nfc, X, Loader2, UserX, ExternalLink, CalendarClock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/core';
import { nfcConnector, type NfcCardReadPayload } from '@/shared/services/nfcConnectorService';
import { lookupWristband, type WristbandLookupResult } from '@/shared/services/wristbandService';
import { useActiveContext } from '@/shared/hooks/useActiveContext';
import { useAuth } from '@/core/auth/useAuth';
import { canOperateActivities } from '@/core/auth/modulePermissions';
import { Button } from '@/theme/components/ui/button';
import { Badge } from '@/theme/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/theme/components/ui/avatar';
import { usePersonProfilePath } from '@/entities/person';
import { GuestServicesActivitiesModal } from '@/modules/services';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ScanState = 'loading' | 'found' | 'unassigned' | 'error';

interface ScanResult {
  state: ScanState;
  lookup?: WristbandLookupResult;
  errorMsg?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const AUTO_DISMISS_MS = 8000;
const RATE_LIMIT_PAUSE_MS = 60_000; // 60s pause after 429

function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function NfcCardScanPopover() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const personProfilePath = usePersonProfilePath();
  // Company scope surfaces enriched identity (staff scope/location, guest
  // booking location); location scope keeps the simple person+role card.
  const { isCompanyContext } = useActiveContext();
  const { user } = useAuth();

  const [visible, setVisible] = useState(false);
  const [result, setResult] = useState<ScanResult>({ state: 'loading' });
  // The Activities & services modal outlives the transient popover, so its
  // trigger snapshots the guest and the popover can auto-dismiss underneath.
  const [showServices, setShowServices] = useState(false);
  const [servicesPerson, setServicesPerson] = useState<{ id: string; name: string } | null>(null);
  // Rate-limit guard tracked in a ref for synchronous reads inside a
  // single `handleCardRead` invocation (state updates are async, so a
  // closure-captured state value would be stale within the handler). It
  // does not drive rendering, so a ref is sufficient.
  const rateLimitedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setVisible(false);
  }, []);

  const startAutoDismiss = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(dismiss, AUTO_DISMISS_MS);
  }, [dismiss]);

  const handleCardRead = useCallback(async (payload: NfcCardReadPayload) => {
    // Skip if connector is busy (WristbandSyncDialog or another operation is active)
    if (nfcConnector.isBusy) return;

    // Skip if rate-limited
    if (rateLimitedRef.current) return;

    nfcConnector.setBusy(true);
    setResult({ state: 'loading' });
    setVisible(true);

    try {
      const identifier = payload.credential || payload.uid;
      const lookup = await lookupWristband(identifier);
      setResult({ state: 'found', lookup });
    } catch (err: unknown) {
      const status = (err as import('@/types/global').ApiError)?.status;
      if (status === 404) {
        setResult({ state: 'unassigned' });
      } else if (status === 429) {
        // Rate limit — show message and pause scanning
        setResult({ state: 'error', errorMsg: t('nfc.scan.rateLimited') });
        rateLimitedRef.current = true;
        nfcConnector.setBusy(true); // Keep busy to prevent more requests
        // Resume scanning after pause
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          rateLimitedRef.current = false;
          nfcConnector.setBusy(false);
          setVisible(false);
        }, RATE_LIMIT_PAUSE_MS);
      } else {
        setResult({ state: 'error', errorMsg: t('nfc.scan.error') });
      }
    } finally {
      if (!rateLimitedRef.current) {
        nfcConnector.setBusy(false);
      }
    }

    if (!rateLimitedRef.current) {
      startAutoDismiss();
    }
  }, [t, startAutoDismiss]);

  useEffect(() => {
    nfcConnector.on('nfc-card-read', handleCardRead as unknown as (p: Record<string, unknown>) => void);
    return () => {
      nfcConnector.off('nfc-card-read', handleCardRead as unknown as (p: Record<string, unknown>) => void);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [handleCardRead]);

  const { state, lookup } = result;
  const person = lookup?.person;
  const role = lookup?.role ?? 'none';

  const roleLabel: Record<string, string> = {
    guest: t('nfc.scan.role.guest', 'Guest'),
    staff: t('nfc.scan.role.staff', 'Staff'),
    none:  t('nfc.scan.role.registered',  'Registered'),
  };
  // Tone per role for the badge — guest is sun (warm welcome),
  // staff is ocean (institutional), none is sand (registered in
  // the system but not currently a guest or staff member).
  const roleBadgeClass: Record<string, string> = {
    guest: 'border-sun-300 bg-sun-100 text-sun-700',
    staff: 'border-ocean-300 bg-ocean-100 text-ocean-700',
    none: 'border-sand-300 bg-sand-100 text-sand-700',
  };

  const profilePath = person
    ? personProfilePath(person.id, role === 'none' ? undefined : role)
    : null;

  const canSeeGuestServices = Boolean(
    role === 'guest' && person && lookup?.guest && canOperateActivities(user, lookup.guest.locationId),
  );

  return (
    <>
      {visible && (
    <div className={cn(
      'fixed top-16 right-4 z-50 w-72 rounded-xl border bg-card shadow-lg',
      'animate-in slide-in-from-top-2 fade-in duration-200',
    )}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Nfc className="h-4 w-4 text-primary" />
          {state === 'loading'
            ? t('nfc.scan.looking', 'Identifying…')
            : state === 'unassigned'
              ? t('nfc.scan.unassigned', 'Unassigned wristband')
              : state === 'error'
                ? t('common.error', 'Error')
                : t('nfc.scan.detected', 'Wristband detected')}
        </div>
        <button
          onClick={dismiss}
          className="text-muted-foreground hover:text-foreground transition-colors"
          aria-label={t('nfc.scan.dismiss', 'Dismiss')}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Body */}
      <div className="px-4 py-3">

        {/* Loading */}
        {state === 'loading' && (
          <div className="flex items-center gap-3 py-2">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {t('nfc.scan.looking', 'Identifying…')}
            </span>
          </div>
        )}

        {/* Unassigned */}
        {state === 'unassigned' && (
          <div className="flex items-center gap-3 py-2">
            <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center shrink-0">
              <UserX className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">
              {t('nfc.scan.unassignedDesc', 'This wristband is not registered in the system')}
            </p>
          </div>
        )}

        {/* Error */}
        {state === 'error' && (
          <p className="text-sm text-destructive py-2">{result.errorMsg}</p>
        )}

        {/* Found — gradient guest card per the redesign mockup. */}
        {state === 'found' && person && (
          <div className="space-y-3">
            <div className="flex items-center gap-3 rounded-md border border-sun-300 bg-gradient-to-br from-sun-50 to-coral-50 p-3">
              <Avatar className="h-9 w-9 shrink-0">
                <AvatarImage src={person.avatarUrl} alt={person.name} />
                <AvatarFallback className="bg-gradient-to-br from-sun-500 to-coral-500 font-serif text-[13px] font-semibold text-white">
                  {getInitials(person.name)}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13.5px] font-semibold text-sand-900">{person.name}</p>
                {person.email && (
                  <p className="mt-0.5 truncate font-mono text-[11px] text-muted-foreground">
                    {person.email}
                  </p>
                )}
              </div>
              <Badge
                variant="outline"
                className={cn(
                  'shrink-0 text-[10px] font-semibold uppercase tracking-wide',
                  roleBadgeClass[role] ?? roleBadgeClass.none,
                )}
              >
                {roleLabel[role] ?? roleLabel.none}
              </Badge>
            </div>

            {/* Enriched identity — company scope only. */}
            {isCompanyContext && lookup && (
              <div className="mt-2 space-y-0.5 rounded-md bg-muted/40 px-2.5 py-2 text-[11px] text-muted-foreground">
                {lookup.staff ? (
                  <p>
                    {lookup.staff.scopeType === 'company'
                      ? t('nfc.scan.staffCompany', 'Company staff')
                      : `${t('nfc.scan.staffLocation', 'Location staff')}${lookup.staff.locationName ? ` · ${lookup.staff.locationName}` : ''}`}
                  </p>
                ) : null}
                {lookup.guest ? (
                  <>
                    <p>{t('nfc.scan.guestAt', 'Guest at')} {lookup.guest.locationName}</p>
                    {/* An expected stay identifies the guest but cannot take a
                        room charge — say so here rather than letting the POS
                        be the first to find out. */}
                    {!lookup.guest.canChargeRoom ? (
                      <p className="text-warning">
                        {t('nfc.scan.guestNotCheckedIn', 'Not checked in — no room charge')}
                      </p>
                    ) : null}
                  </>
                ) : null}
                {!lookup.staff && !lookup.guest ? (
                  <p>{t('nfc.scan.personOnly', 'Person — no active staff or guest role')}</p>
                ) : null}
              </div>
            )}

            {canSeeGuestServices && person && (
              <Button
                size="sm"
                className="w-full bg-ocean-500 text-white hover:bg-ocean-600"
                onClick={() => {
                  if (timerRef.current) clearTimeout(timerRef.current);
                  setServicesPerson({ id: person.id, name: person.name });
                  setShowServices(true);
                  setVisible(false);
                }}
              >
                <CalendarClock className="mr-1.5 h-3.5 w-3.5" />
                {t('nfc.scan.activitiesServices', 'Activities & services')}
              </Button>
            )}

            {profilePath && (
              <Button
                size="sm"
                variant="outline"
                className="w-full"
                onClick={() => { navigate(profilePath); dismiss(); }}
              >
                <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                {t('nfc.scan.viewProfile', 'View profile')}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Auto-dismiss progress bar */}
      {state !== 'loading' && (
        <div className="px-4 pb-3">
          <div className="h-0.5 w-full bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary/40 rounded-full"
              style={{ animation: `shrink ${AUTO_DISMISS_MS}ms linear forwards` }}
            />
          </div>
        </div>
      )}

      <style>{`
        @keyframes shrink {
          from { width: 100%; }
          to   { width: 0%; }
        }
      `}</style>
    </div>
      )}

      <GuestServicesActivitiesModal
        open={showServices}
        onOpenChange={(o) => { setShowServices(o); if (!o) setServicesPerson(null); }}
        personId={servicesPerson?.id}
        personName={servicesPerson?.name}
      />
    </>
  );
}
